import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import dayjs from 'dayjs';

const DownloadEmployeeAttendanceReportPdf = (apiResponse, companyName, logo, setOpenBackdrop, dateRange) => {
    // 1. Validate API Response Data
    if (!apiResponse || !apiResponse.summary || !Array.isArray(apiResponse.daily_report)) {
        alert("Invalid or empty data found for this employee!");
        return;
    }

    const { summary, daily_report: dailyReport } = apiResponse;
    const doc = new jsPDF('p', 'mm', 'a4'); // Single employee ke liye Portrait layout best hai
    if (typeof setOpenBackdrop === 'function') setOpenBackdrop(true);

    try {
        const pageWidth = doc.internal.pageSize.getWidth(); // A4 Portrait width (~210mm)

        // --- 1. Header Section (Company Info) ---
        if (logo) {
            doc.addImage(logo, 'PNG', 14, 10, 15, 15);
        }
        
        doc.setFontSize(18);
        doc.setTextColor(25, 118, 210); // Professional Blue
        doc.setFont("helvetica", "bold");
        doc.text(companyName || 'COMPANY NAME', logo ? 35 : 14, 19);
        
        // Horizontal line under company header
        doc.setDrawColor(220);
        doc.line(14, 28, pageWidth - 14, 28);

        // --- 2. Center Headers (Title & Date Range) ---
        doc.setFontSize(14);
        doc.setTextColor(60, 60, 60);
        doc.setFont("helvetica", "bold");
        doc.text('EMPLOYEE ATTENDANCE REPORT', pageWidth / 2, 36, { align: 'center' });

        const startDate = dateRange && dateRange[0] ? dayjs(dateRange[0]).format("DD-MM-YYYY") : dayjs(summary.from_date).format("DD-MM-YYYY");
        const endDate = dateRange && dateRange[1] ? dayjs(dateRange[1]).format("DD-MM-YYYY") : dayjs(summary.to_date).format("DD-MM-YYYY");
        
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.setFont("helvetica", "normal");
        doc.text(`Period: ${startDate} to ${endDate}`, pageWidth / 2, 42, { align: 'center' });

        // --- 3. Employee Meta Summary Box ---
        // Light grey background container card for summary stats
        doc.setFillColor(248, 249, 250);
        doc.rect(14, 48, pageWidth - 28, 24, 'F');
        doc.setDrawColor(230);
        doc.rect(14, 48, pageWidth - 28, 24, 'D');

        // Left Side: Info
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(50);
        doc.text(`Employee Name: ${summary.employee_name}`, 18, 54);
        doc.setFont("helvetica", "normal");
        doc.text(`Total Period Days: ${summary.total_days}`, 18, 60);
        doc.text(`Employee ID: ${summary.employee_id || 'N/A'}`, 18, 66);

        // Right Side: Quick Attendance Counter Metrics
        let metricsXStart = 120;
        doc.setFont("helvetica", "bold");
        
        doc.setTextColor(46, 125, 50); // Green
        doc.text(`Present : ${summary.present}`, metricsXStart, 54);
        
        doc.setTextColor(211, 47, 47); // Red
        doc.text(`Absent  : ${summary.absent}`, metricsXStart, 60);
        
        doc.setTextColor(237, 108, 2); // Orange
        doc.text(`Leave    : ${summary.leave}`, metricsXStart + 45, 54);
        
        doc.setTextColor(2, 136, 209); // Blue
        doc.text(`Holiday : ${summary.holiday}`, metricsXStart + 45, 60);

        // --- 4. Daily Report Table Structure ---
        const tableHeaders = [['S.No', 'Date', 'Day', 'Attendance Status']];
        
        const tableData = dailyReport.map((item, idx) => [
            idx + 1,
            dayjs(item.date).format("DD-MM-YYYY"),
            item.day || '—',
            item.status || '—'
        ]);

        // --- 5. Generate Table ---
        doc.autoTable({
            startY: 78,
            head: tableHeaders,
            body: tableData,
            theme: 'striped',
            headStyles: { 
                fillColor: [25, 118, 210], 
                textColor: [255, 255, 255],
                fontSize: 10,
                fontStyle: 'bold',
                halign: 'center'
            },
            styles: { 
                fontSize: 9.5, 
                cellPadding: 3,
                halign: 'center' 
            },
            columnStyles: {
                0: { cellWidth: 20 }, // S.No
                1: { cellWidth: 45 }, // Date
                2: { cellWidth: 55 }, // Day
                3: { 
                    cellWidth: 62, 
                    fontStyle: 'bold'
                } // Status Column
            },
            // Row styling based on status color coding dynamically
            didParseCell: (data) => {
                if (data.section === 'body' && data.column.index === 3) {
                    const statusText = data.cell.raw;
                    if (statusText === 'Present') {
                        data.cell.styles.textColor = [46, 125, 50];
                    } else if (statusText === 'Absent') {
                        data.cell.styles.textColor = [211, 47, 47];
                    } else if (statusText === 'Holiday') {
                        data.cell.styles.textColor = [2, 136, 209];
                    } else if (statusText === 'Leave') {
                        data.cell.styles.textColor = [237, 108, 2];
                    }
                }
            },
            didDrawPage: (data) => {
                const totalPages = doc.internal.getNumberOfPages();
                doc.setFontSize(8);
                doc.setTextColor(150);
                doc.setFont("helvetica", "normal");
                doc.text(`Page ${totalPages}`, pageWidth - 14, 287, { align: 'right' });
                doc.text(`Generated on: ${dayjs().format("DD-MM-YYYY HH:mm:ss")}`, 14, 287);
            }
        });

        // --- 6. Save PDF ---
        const sanitizedEmpName = summary.employee_name.replace(/\s+/g, '_');
        doc.save(`${sanitizedEmpName}_Attendance_Report_${dayjs().format("YYYYMMDD")}.pdf`);

    } catch (error) {
        console.error("PDF Generation Error:", error);
        alert("Something went wrong while generating the employee PDF");
    } finally {
        if (typeof setOpenBackdrop === 'function') setOpenBackdrop(false);
    }
};

export default DownloadEmployeeAttendanceReportPdf;